import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  FaRobot,
  FaCheckCircle,
  FaTimesCircle,
  FaPenSquare,
  FaArrowDown,
  FaArrowUp,
  FaSync,
  FaClock,
  FaBolt,
  FaDollarSign,
  FaExclamationTriangle,
} from "react-icons/fa";
import { auth, db } from "../../firebase";
import DataTable from "../../components/DataTable";
import EmptyState from "../../components/EmptyState";
import Loader from "../../components/Loader";
import AdminModal from "../../components/AdminModal";
import { useToast } from "../../components/Toast";
import {
  colors,
  spacing,
  typography,
  radius,
  shadows,
  pageContainer,
  pageTitle,
  pageSubtitle,
} from "../../utils/designTokens";

/**
 * SuperAdmin: monitor de llamadas a agentes IA internos.
 *
 * Lee de la colección `ai_logs` que se popula desde el backend `propia_agent`
 * via el módulo ai_telemetry.py. Cada llamada a un LLM (chat con el agente,
 * procesamiento de facturas, mejora de mensajes, transformación de novedades,
 * extracción de unidades) deja un registro con: capability, user, building,
 * tokens, latencia, costo, prompt y response truncados.
 *
 * Casos de uso:
 *  - Validar que el agente está respondiendo cosas correctas (ver pregunta vs respuesta).
 *  - Detectar errores y patrones de fallo.
 *  - Monitorear costo y latencia.
 *  - Marcar interacciones como buena/mala/necesita-edición → input para mejorar prompts.
 */

const VERDICTS = {
  good: { label: "Buena", icon: FaCheckCircle, color: colors.success, bg: colors.successLight },
  bad: { label: "Mala", icon: FaTimesCircle, color: colors.error, bg: colors.errorLight },
  needs_edit: { label: "Editar", icon: FaPenSquare, color: colors.warningDark, bg: colors.warningLight },
};

const CAPABILITY_LABELS = {
  agent_chat: "Chat (residente)",
  agent_chat_multi: "Chat (admin multi-edif.)",
  process_invoice: "Procesar factura",
  process_units: "Procesar unidades",
  improve_message: "Mejorar mensaje",
  transform_novedad: "Transformar novedad",
};

const labelFor = (cap) => CAPABILITY_LABELS[cap] || cap || "—";

const MonitorAgentesIA = () => {
  const { showToast } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCapability, setFilterCapability] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterReview, setFilterReview] = useState("all"); // all | pending | reviewed
  const [selected, setSelected] = useState(null);
  const [reviewVerdict, setReviewVerdict] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [savingReview, setSavingReview] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "ai_logs"),
        orderBy("timestamp", "desc"),
        limit(200)
      );
      const snap = await getDocs(q);
      setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("MonitorAgentesIA fetch error:", e);
      showToast("Error cargando logs de IA", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Capabilities únicas presentes en los datos (para los chips de filtro)
  const presentCapabilities = useMemo(() => {
    const set = new Set();
    logs.forEach((l) => l.capability && set.add(l.capability));
    return Array.from(set).sort();
  }, [logs]);

  const filtered = useMemo(() => {
    return logs.filter((l) => {
      if (filterCapability !== "all" && l.capability !== filterCapability) return false;
      if (filterStatus !== "all" && l.status !== filterStatus) return false;
      if (filterReview === "pending" && l.reviewVerdict) return false;
      if (filterReview === "reviewed" && !l.reviewVerdict) return false;
      return true;
    });
  }, [logs, filterCapability, filterStatus, filterReview]);

  // KPIs sobre todo el dataset
  const kpis = useMemo(() => {
    const total = logs.length;
    const errors = logs.filter((l) => l.status === "error").length;
    const totalCost = logs.reduce((acc, l) => acc + (l.cost_usd_estimated || 0), 0);
    const avgLatency = total > 0
      ? Math.round(logs.reduce((acc, l) => acc + (l.latency_ms || 0), 0) / total)
      : 0;
    const cacheHits = logs.filter((l) => l.tokens_cached_read).length;
    const cacheRate = total > 0 ? Math.round((cacheHits / total) * 100) : 0;
    const errorRate = total > 0 ? Math.round((errors / total) * 100) : 0;
    return { total, errors, totalCost, avgLatency, cacheRate, errorRate };
  }, [logs]);

  const openDetail = (logEntry) => {
    setSelected(logEntry);
    setReviewVerdict(logEntry.reviewVerdict || null);
    setReviewNotes(logEntry.reviewNotes || "");
  };

  const closeDetail = () => {
    if (savingReview) return;
    setSelected(null);
    setReviewVerdict(null);
    setReviewNotes("");
  };

  const saveReview = async () => {
    if (!selected || !reviewVerdict) {
      showToast("Elegí un verdict (Buena/Mala/Editar) primero.", "warning");
      return;
    }
    setSavingReview(true);
    try {
      const reviewer = auth.currentUser?.email || auth.currentUser?.uid || "unknown";
      await updateDoc(doc(db, "ai_logs", selected.id), {
        reviewVerdict,
        reviewNotes: reviewNotes.trim() || null,
        reviewedBy: reviewer,
        reviewedAt: serverTimestamp(),
      });
      showToast("Revisión guardada.", "success");
      closeDetail();
      await fetchLogs();
    } catch (e) {
      console.error("saveReview error:", e);
      showToast(`Error: ${e.message}`, "error");
    } finally {
      setSavingReview(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncate = (s, n = 80) => {
    if (!s) return "—";
    return s.length > n ? s.slice(0, n) + "…" : s;
  };

  const formatCost = (c) => (c == null ? "—" : `$${c.toFixed(4)}`);
  const formatLatency = (ms) => (!ms ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

  const columns = [
    {
      key: "timestamp",
      label: "Cuando",
      render: (val) => (
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, whiteSpace: "nowrap" }}>
          {formatTimestamp(val)}
        </span>
      ),
    },
    {
      key: "capability",
      label: "Capability",
      render: (val) => (
        <span style={{
          display: "inline-block",
          padding: "2px 8px",
          background: colors.primaryLighter,
          color: colors.primary,
          borderRadius: radius.full,
          fontSize: 12,
        }}>
          {labelFor(val)}
        </span>
      ),
    },
    {
      key: "user_email",
      label: "Quién",
      render: (val, row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>
            {val || row.user_id || "—"}
          </span>
          {row.building_id && (
            <span style={{ fontSize: 11, color: colors.gray400 }}>
              Edif. {String(row.building_id).slice(0, 16)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "prompt_summary",
      label: "Pregunta / Input",
      render: (val) => (
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>
          {truncate(val, 70)}
        </span>
      ),
    },
    {
      key: "response_summary",
      label: "Respuesta",
      render: (val, row) => (
        <span style={{
          fontSize: typography.sizes.sm,
          color: row.status === "error" ? colors.error : colors.gray700,
          fontStyle: row.status === "error" ? "italic" : "normal",
        }}>
          {truncate(val || (row.error_message ? `Error: ${row.error_message}` : ""), 70)}
        </span>
      ),
    },
    {
      key: "latency_ms",
      label: "Latencia",
      align: "right",
      render: (val) => (
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, whiteSpace: "nowrap" }}>
          {formatLatency(val)}
        </span>
      ),
    },
    {
      key: "cost_usd_estimated",
      label: "Costo",
      align: "right",
      render: (val) => (
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, whiteSpace: "nowrap" }}>
          {formatCost(val)}
        </span>
      ),
    },
    {
      key: "reviewVerdict",
      label: "Revisión",
      align: "center",
      render: (val) => {
        if (!val) {
          return (
            <span style={{
              fontSize: typography.sizes.sm,
              padding: "2px 10px",
              background: colors.gray100,
              color: colors.gray600,
              borderRadius: radius.full,
            }}>
              Pendiente
            </span>
          );
        }
        const cfg = VERDICTS[val] || { label: val, icon: FaCheckCircle, color: colors.gray600, bg: colors.gray100 };
        const Icon = cfg.icon;
        return (
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "2px 10px",
            background: cfg.bg,
            color: cfg.color,
            borderRadius: radius.full,
            fontSize: typography.sizes.sm,
          }}>
            <Icon size={11} />
            {cfg.label}
          </span>
        );
      },
    },
  ];

  if (loading && logs.length === 0) return <Loader />;

  return (
    <div style={pageContainer}>
      <div style={headerStyle}>
        <div>
          <h1 style={pageTitle}>Monitor de agentes IA</h1>
          <p style={{ ...pageSubtitle, marginTop: 4, maxWidth: 720 }}>
            Cada llamada interna a un modelo IA (chat, facturas, comunicados, etc.) se loguea acá
            con su pregunta, respuesta, latencia y costo. Sirve para validar calidad,
            detectar errores y mejorar prompts.
          </p>
        </div>
        <button onClick={fetchLogs} style={ghostBtnStyle}>
          <FaSync size={11} />
          Actualizar
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: spacing[3], marginBottom: spacing[5], flexWrap: "wrap" }}>
        <KpiCard label="Total llamadas" value={kpis.total} icon={FaRobot} />
        <KpiCard
          label="Costo total"
          value={`$${kpis.totalCost.toFixed(4)}`}
          icon={FaDollarSign}
          color={colors.gray900}
        />
        <KpiCard
          label="Latencia media"
          value={formatLatency(kpis.avgLatency)}
          icon={FaClock}
        />
        <KpiCard
          label="Cache hit rate"
          value={`${kpis.cacheRate}%`}
          icon={FaBolt}
          color={kpis.cacheRate >= 30 ? colors.success : colors.gray900}
        />
        <KpiCard
          label="% errores"
          value={`${kpis.errorRate}%`}
          icon={FaExclamationTriangle}
          color={kpis.errorRate > 5 ? colors.error : colors.gray900}
        />
      </div>

      {/* Filtros — capability */}
      <div style={{ display: "flex", gap: spacing[2], marginBottom: spacing[2], flexWrap: "wrap" }}>
        <FilterChip active={filterCapability === "all"} onClick={() => setFilterCapability("all")}>
          Todas las capabilities
        </FilterChip>
        {presentCapabilities.map((cap) => (
          <FilterChip
            key={cap}
            active={filterCapability === cap}
            onClick={() => setFilterCapability(cap)}
          >
            {labelFor(cap)}
          </FilterChip>
        ))}
      </div>

      {/* Filtros — status / review */}
      <div style={{ display: "flex", gap: spacing[2], marginBottom: spacing[4], flexWrap: "wrap" }}>
        {[
          { id: "all", label: "Todos los status" },
          { id: "success", label: "Solo OK" },
          { id: "error", label: "Solo errores" },
        ].map((f) => (
          <FilterChip
            key={f.id}
            active={filterStatus === f.id}
            onClick={() => setFilterStatus(f.id)}
            small
          >
            {f.label}
          </FilterChip>
        ))}
        <span style={{ width: 1, background: colors.gray200, alignSelf: "stretch", margin: "0 4px" }} />
        {[
          { id: "all", label: "Cualquier revisión" },
          { id: "pending", label: "Pendiente revisar" },
          { id: "reviewed", label: "Ya revisadas" },
        ].map((f) => (
          <FilterChip
            key={f.id}
            active={filterReview === f.id}
            onClick={() => setFilterReview(f.id)}
            small
          >
            {f.label}
          </FilterChip>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={FaRobot}
          title={logs.length === 0 ? "Todavía no hay llamadas IA registradas" : "No hay logs en este filtro"}
          description={
            logs.length === 0
              ? "Cuando algún staff o residente use IA (chat, factura, comunicado…) aparecerá acá."
              : "Probá con otro filtro o esperá nuevas llamadas."
          }
        />
      ) : (
        <DataTable columns={columns} data={filtered} onRowClick={openDetail} />
      )}

      <AdminModal
        open={!!selected}
        onClose={closeDetail}
        title="Detalle de llamada IA"
        size="wide"
        icon={<FaRobot size={20} color={colors.primary} />}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button onClick={closeDetail} disabled={savingReview} style={ghostBtnStyle}>
              Cancelar
            </button>
            <button onClick={saveReview} disabled={savingReview || !reviewVerdict} style={primaryBtnStyle}>
              {savingReview ? "Guardando…" : "Guardar revisión"}
            </button>
          </div>
        }
      >
        {selected && (
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
            {/* Capability + status */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{
                padding: "4px 12px",
                background: colors.primaryLighter,
                color: colors.primary,
                borderRadius: radius.full,
                fontSize: typography.sizes.base,
              }}>
                {labelFor(selected.capability)}
              </span>
              <span style={{
                padding: "4px 12px",
                background: selected.status === "error" ? colors.errorLight : colors.successLight,
                color: selected.status === "error" ? colors.error : colors.success,
                borderRadius: radius.full,
                fontSize: typography.sizes.base,
              }}>
                {selected.status === "error" ? "Error" : "OK"}
              </span>
              <span style={{ color: colors.gray600, fontSize: typography.sizes.sm }}>
                {formatTimestamp(selected.timestamp)}
              </span>
            </div>

            {/* Pregunta / input */}
            <div>
              <div style={smallLabel}>
                <FaArrowDown size={10} /> Pregunta / input
              </div>
              <div style={{ ...messageBubbleStyle, background: colors.gray100 }}>
                {selected.prompt_summary || "(no se logueó el contenido — ver metadata abajo)"}
              </div>
            </div>

            {/* Respuesta del agente */}
            <div>
              <div style={smallLabel}>
                <FaArrowUp size={10} /> Respuesta
              </div>
              <div style={{
                ...messageBubbleStyle,
                background: selected.status === "error" ? colors.errorLight : "#DCF8C6",
                color: selected.status === "error" ? colors.errorDark : colors.gray900,
              }}>
                {selected.response_summary || (selected.error_message ? `Error: ${selected.error_message}` : "(no se logueó la respuesta)")}
              </div>
            </div>

            {/* Métricas técnicas */}
            <div style={metricsGrid}>
              <Metric label="Modelo" value={`${selected.provider || "—"} / ${selected.model || "—"}`} />
              <Metric label="Latencia" value={formatLatency(selected.latency_ms)} />
              <Metric label="Costo estimado" value={formatCost(selected.cost_usd_estimated)} />
              <Metric label="Tokens entrada" value={selected.tokens_input ?? "—"} />
              <Metric label="Tokens salida" value={selected.tokens_output ?? "—"} />
              <Metric label="Cache hit (read)" value={selected.tokens_cached_read ?? "—"} />
              <Metric label="Cache write" value={selected.tokens_cached_write ?? "—"} />
              <Metric label="Canal" value={selected.channel || "—"} />
            </div>

            {/* Contexto */}
            <div style={{
              padding: spacing[3],
              background: colors.gray50,
              borderRadius: radius.sm,
              fontSize: typography.sizes.sm,
              color: colors.gray700,
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "4px 12px",
            }}>
              <span style={{ color: colors.gray600 }}>Usuario:</span>
              <span>{selected.user_email || `userid ${selected.user_id || "—"}`}</span>
              <span style={{ color: colors.gray600 }}>Edificio:</span>
              <span>{selected.building_id || "—"}</span>
              {selected.metadata && (
                <>
                  <span style={{ color: colors.gray600 }}>Metadata:</span>
                  <code style={{ fontSize: 11, whiteSpace: "pre-wrap" }}>
                    {JSON.stringify(selected.metadata, null, 2)}
                  </code>
                </>
              )}
              {selected.reviewedBy && (
                <>
                  <span style={{ color: colors.gray600 }}>Revisado por:</span>
                  <span>{selected.reviewedBy} · {formatTimestamp(selected.reviewedAt)}</span>
                </>
              )}
            </div>

            {/* Verdict */}
            <div>
              <div style={{ ...smallLabel, marginBottom: spacing[2] }}>Tu evaluación (opcional)</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Object.entries(VERDICTS).map(([id, cfg]) => {
                  const Icon = cfg.icon;
                  const active = reviewVerdict === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setReviewVerdict(id)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 14px",
                        fontSize: typography.sizes.base,
                        fontWeight: 400,
                        background: active ? cfg.color : colors.white,
                        color: active ? colors.white : cfg.color,
                        border: `1px solid ${cfg.color}`,
                        borderRadius: radius.sm,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      <Icon size={12} />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notas */}
            <div>
              <div style={{ ...smallLabel, marginBottom: spacing[1] }}>Notas (opcional)</div>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder='Ej: "Inventó una fecha", "Falta contexto", "Respuesta correcta y precisa", "Demoró 18s, hay que optimizar"'
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: typography.sizes.base,
                  fontWeight: 400,
                  color: colors.gray900,
                  background: colors.white,
                  border: `1px solid ${colors.gray300}`,
                  borderRadius: radius.sm,
                  outline: "none",
                  fontFamily: "inherit",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        )}
      </AdminModal>
    </div>
  );
};

const KpiCard = ({ label, value, color, icon: Icon }) => (
  <div style={{
    padding: `${spacing[3]}px ${spacing[4]}px`,
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.xs,
    minWidth: 180,
    flex: "1 1 180px",
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
  }}>
    {Icon && (
      <div style={{
        width: 36, height: 36,
        borderRadius: radius.sm,
        background: colors.primaryLighter,
        color: colors.primary,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={14} />
      </div>
    )}
    <div>
      <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>{label}</div>
      <div style={{
        fontSize: typography.sizes.lg,
        fontWeight: 400,
        color: color || colors.gray900,
      }}>
        {value}
      </div>
    </div>
  </div>
);

const FilterChip = ({ active, onClick, small, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: small ? "6px 12px" : "8px 16px",
      fontSize: small ? typography.sizes.sm : typography.sizes.base,
      fontWeight: 400,
      background: active ? colors.primary : colors.white,
      color: active ? colors.white : colors.gray700,
      border: `1px solid ${active ? colors.primary : colors.gray300}`,
      borderRadius: radius.sm,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "all 0.15s ease",
    }}
  >
    {children}
  </button>
);

const Metric = ({ label, value }) => (
  <div style={{
    padding: spacing[2],
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
  }}>
    <div style={{ fontSize: 11, color: colors.gray600 }}>{label}</div>
    <div style={{ fontSize: typography.sizes.base, color: colors.gray900, marginTop: 2 }}>
      {value}
    </div>
  </div>
);

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: spacing[4],
  gap: spacing[4],
};

const ghostBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.gray700,
  background: colors.white,
  border: `1px solid ${colors.gray300}`,
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: "inherit",
};

const primaryBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 18px",
  fontSize: typography.sizes.base,
  fontWeight: 400,
  color: colors.white,
  background: colors.primary,
  border: "none",
  borderRadius: radius.sm,
  cursor: "pointer",
  fontFamily: "inherit",
};

const smallLabel = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: typography.sizes.sm,
  color: colors.gray600,
  marginBottom: 4,
};

const messageBubbleStyle = {
  padding: spacing[3],
  borderRadius: radius.md,
  fontSize: typography.sizes.base,
  lineHeight: 1.5,
  color: colors.gray900,
  whiteSpace: "pre-wrap",
};

const metricsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
  gap: spacing[2],
};

export default MonitorAgentesIA;
